import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/axios.ts';
import { toast } from 'react-hot-toast';
import getApiErrorMessage from '../utils/helper.js';

export interface MemberLoan {
  id: number;
  member_id: number;
  loan_type_id: number;
  group_id: number;
  start_date: string;
  end_date: string;
  duration: number;
  principal: number;
  reason: string;
  status: string;
  loan_balance: number;
  total_amount: number;
  // Approval workflow fields
  applied_by?: number;
  approved_by?: number;
  disbursed_by?: number;
  applied_at?: string;
  approved_at?: string;
  disbursed_at?: string;
  approval_notes?: string;
  disbursement_notes?: string;
  member?: { id: number; name: string };
  loan_type?: { id: number; name: string };
  // Approval workflow user relationships
  appliedByUser?: { id: number; name: string };
  approvedByUser?: { id: number; name: string };
  disbursedByUser?: { id: number; name: string };
  repayments?: Array<{
    id: number;
    amount: number;
    payment_date: string;
    payment_method: string;
    description: string;
  }>;
  repayments_count?: number;
  total_repaid?: number;
  last_repayment_date?: string;
}

export interface MemberLoanFilters {
  search?: string;
  member_id?: number;
  loan_type_id?: number;
  status?: string;
  start_date_from?: string;
  start_date_to?: string;
  end_date_from?: string;
  end_date_to?: string;
  repayment_date_from?: string;
  repayment_date_to?: string;
  has_repayments?: string;
  principal_min?: number;
  principal_max?: number;
  due_today?: string;
  due_date?: string;
  // Approval workflow filters
  approval_status?: string;
  applied_date_from?: string;
  applied_date_to?: string;
  approved_date_from?: string;
  approved_date_to?: string;
  disbursed_date_from?: string;
  disbursed_date_to?: string;
  applied_by?: number;
  approved_by?: number;
  disbursed_by?: number;
  page?: number;
  per_page?: number;
}

export const useMemberLoans = (filters: MemberLoanFilters = {}) =>
  useQuery({
    queryKey: ['memberLoans', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
      
      const response = await api.get(`/memberloan?${params.toString()}`);
      return response.data;
    },
    staleTime: 30000, // 30 seconds
  });

// Hook to export loans
export const useExportLoans = () => {
  return useMutation({
    mutationFn: async ({filters = {}, format = 'excel'}: {filters?: MemberLoanFilters, format?: 'excel' | 'pdf' | 'csv'}) => {
      const params = new URLSearchParams();
      
      // Add format parameter
      params.append('type', format);
      
      // Add filter parameters
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
      
      const response = await api.get(`/memberloan/export?${params.toString()}`, {
        responseType: 'blob',
      });
      
      // Determine file extension based on format
      const extension = format === 'pdf' ? 'pdf' : format === 'csv' ? 'csv' : 'xlsx';
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `loans-${new Date().toISOString().split('T')[0]}.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });
};

export const useExportCollectionSheet = () => {
  return useMutation({
    mutationFn: async ({ filters = {}, date }: { filters?: MemberLoanFilters; date?: string }) => {
      const params = new URLSearchParams();
      const reportDate = date || new Date().toISOString().split('T')[0];
      params.append('date', reportDate);

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });

      const response = await api.get(`/memberloan/collection-sheet/export?${params.toString()}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `collection-sheet-${reportDate}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });
};

export const useCreateMemberLoan = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (loan: Omit<MemberLoan, 'id'> | FormData) => {
      const config = loan instanceof FormData ? {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      } : {};
      
      const { data } = await api.post('/memberloan', loan, config);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['member-loans'] });
      queryClient.invalidateQueries({ queryKey: ['pending-approvals'] });
      queryClient.invalidateQueries({ queryKey: ['loan-approval-dashboard-stats'] });
      toast.success('Loan created successfully');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });
};

export const useUpdateMemberLoan = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, loan }: { id: number; loan: FormData | any }) => {
      if (loan instanceof FormData) {
        // Add _method=PUT for Laravel to recognize this as a PUT request
        loan.append('_method', 'PUT');
        
        const config = {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        };
        
        // Use POST with _method=PUT for file uploads
        const { data } = await api.post(`/memberloan/${id}`, loan, config);
        return data;
      } else {
        // Use regular PUT for non-FormData updates
        const { data } = await api.put(`/memberloan/${id}`, loan);
        return data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['member-loans'] });
      queryClient.invalidateQueries({ queryKey: ['member-loan'] });
      queryClient.invalidateQueries({ queryKey: ['pending-approvals'] });
      queryClient.invalidateQueries({ queryKey: ['approval-history'] });
      queryClient.invalidateQueries({ queryKey: ['loan-approval-dashboard-stats'] });
      toast.success('Loan updated successfully');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });
};

export const useMemberLoan = (id: number) => {
  return useQuery({
    queryKey: ['member-loan', id],
    queryFn: async () => {
      const { data } = await api.get(`/memberloan/${id}`);
      return data;
    },
    enabled: !!id,
  });
};

// Hook to get approval history for a specific loan
export const useMemberLoanApprovalHistory = (id: number) => {
  return useQuery({
    queryKey: ['member-loan-approval-history', id],
    queryFn: async () => {
      const { data } = await api.get(`/memberloan/${id}/approval-history`);
      return data;
    },
    enabled: !!id,
    staleTime: 300000, // 5 minutes
  });
};

export const useDeleteMemberLoan = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/memberloan/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['member-loans'] });
      queryClient.invalidateQueries({ queryKey: ['pending-approvals'] });
      queryClient.invalidateQueries({ queryKey: ['approval-history'] });
      queryClient.invalidateQueries({ queryKey: ['loan-approval-dashboard-stats'] });
      toast.success('Loan deleted successfully');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });
}; 

export function useMemberLoanStatistics(filters = {}) {
  return useQuery({
    queryKey: ['member-loan-statistics', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      
      // Add filters to query params
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });

      const response = await api.get(`/memberloan/statistics?${params.toString()}`);
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
} 